import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { mensajeDeError } from '@/lib/errores';
import { confirmAsync } from '@/lib/confirm';
import {
  enlaceListaClientes, leerListaClientes, parametrosListaClientes,
} from '@/lib/listaClientes';
import { describirFicha, hayQueMirarNif, preguntaAlGuardar } from '@/lib/nifRepetido';
import { AlertTriangle, Building2, Mail, Phone, MapPin, Search, Edit, Filter, Upload, FileSpreadsheet, RefreshCw, Plus } from 'lucide-react';

export default function Clients({ user }) {
  const [clients, setClients] = useState([]);
  // Búsqueda y filtros arrancan de la URL: es lo que permite volver de una ficha
  // al mismo sitio. Ver lib/listaClientes.js.
  const [searchParams, setSearchParams] = useSearchParams();
  const [inicial] = useState(() => leerListaClientes(searchParams));
  const [searchTerm, setSearchTerm] = useState(inicial.buscar);
  const [typeFilter, setTypeFilter] = useState(inicial.tipo);
  const [statusFilter, setStatusFilter] = useState(inicial.estado);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  // El diálogo es el mismo para editar y para dar de alta.
  const [creandoCliente, setCreandoCliente] = useState(false);
  // Otras fichas con el NIF escrito. undefined = no se ha mirado; null = no se
  // ha podido mirar; [] = ninguna.
  const [fichasMismoNif, setFichasMismoNif] = useState(undefined);
  const [crmStatuses, setCrmStatuses] = useState([]);
  // null = todavia no se sabe o ha fallado la consulta; [] = ninguna
  const [bajasConPlantilla, setBajasConPlantilla] = useState(null);
  const [bajasError, setBajasError] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Xero import (CSV de Xero Contacts)
  const xeroFileRef = useRef(null);
  const [xeroImporting, setXeroImporting] = useState(false);
  const [xeroResult, setXeroResult] = useState(null);
  const [xeroDialogOpen, setXeroDialogOpen] = useState(false);
  
  // Paginación
  const [page, setPage] = useState(inicial.pagina);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  const listaActual = { buscar: searchTerm, tipo: typeFilter, estado: statusFilter, pagina: page };

  useEffect(() => {
    setSearchParams(parametrosListaClientes(listaActual), { replace: true });
  }, [searchTerm, typeFilter, statusFilter, page]);

  // Debounce para búsqueda
  const busquedaSinTocar = useRef(true);
  useEffect(() => {
    // Al entrar no ha buscado nadie: la carga inicial la hace el efecto de abajo,
    // y volver a la página 1 aquí perdería la página a la que se vuelve.
    if (busquedaSinTocar.current) {
      busquedaSinTocar.current = false;
      return undefined;
    }
    const debounceTimer = setTimeout(() => {
      setPage(1); // Reset a página 1 cuando se busca
      fetchClients();
    }, 500); // 500ms de debounce

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    fetchClients();
    fetchCrmStatuses();
  }, [page, typeFilter, statusFilter]);

  useEffect(() => {
    fetchBajasConPlantilla();
  }, []);

  const fetchBajasConPlantilla = async () => {
    try {
      const { data } = await axiosInstance.get('/customers/bajas-con-plantilla');
      setBajasConPlantilla(data);
      setBajasError(false);
    } catch (error) {
      // Sin esto, un fallo se leeria como que no hay ninguna.
      setBajasConPlantilla(null);
      setBajasError(true);
    }
  };

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      // Agregar búsqueda global
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axiosInstance.get(`/customers?${params.toString()}`);
      const data = response.data;
      
      // Si es el nuevo formato con paginación
      if (data.items) {
        setClients(data.items);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      } else {
        // Formato antiguo (array directo)
        setClients(data);
      }
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const fetchCrmStatuses = async () => {
    try {
      const response = await axiosInstance.get('/crm-statuses');
      setCrmStatuses(response.data);
    } catch (error) {
      console.error('Error loading CRM statuses');
    }
  };

  const navigate = useNavigate();

  const handleEdit = (client) => {
    setEditingClient(client);
    setCreandoCliente(false);
    setFichasMismoNif(undefined);
    setDialogOpen(true);
  };

  // Alta directa, sin pasar por el CRM (Myriam, oct-2026). Nace cliente y
  // Activo; la plataforma, el DNS y el mandato se hacen luego desde su ficha.
  const handleNuevo = () => {
    setEditingClient({ type: 'customer', country: 'España' });
    setCreandoCliente(true);
    setFichasMismoNif(undefined);
    setDialogOpen(true);
  };

  /** Otras fichas con este NIF, o null si no se ha podido preguntar. */
  const buscarMismoNif = async (nif) => {
    try {
      // POST: con el NIF en la URL saldría en el log de accesos.
      const { data } = await axiosInstance.post('/customers/nif-repetido', {
        nif, excluir: creandoCliente ? null : editingClient?.id,
      });
      return data.fichas;
    } catch (error) {
      return null;
    }
  };

  const nifTocado = (nif) => hayQueMirarNif({
    creando: creandoCliente, nuevo: nif, original: editingClient?.tax_id,
  });

  const alSalirDelNif = async (e) => {
    const nif = e.target.value;
    setFichasMismoNif(nifTocado(nif) ? await buscarMismoNif(nif) : undefined);
  };

  const handleViewDetail = (clientId) => {
    navigate(`/clients/${clientId}`, { state: { volverA: enlaceListaClientes(listaActual) } });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      company: formData.get('company'),
      tax_id: formData.get('tax_id'),
      address: formData.get('address'),
      postal_code: formData.get('postal_code'),
      city: formData.get('city'),
      country: formData.get('country'),
      billing_contact_name: formData.get('billing_contact_name'),
      billing_contact_phone: formData.get('billing_contact_phone'),
      notes: formData.get('notes'),
      type: formData.get('type'),
      billing_note: formData.get('billing_note'),
      show_billing_alert: formData.get('show_billing_alert') === 'on',
      sepa_iban: formData.get('sepa_iban') || null,
      sepa_bic: formData.get('sepa_bic') || null,
      trade_name: formData.get('trade_name') || null,
      fiscal_name: formData.get('fiscal_name') || null,
      billing_email: formData.get('billing_email') || null,
      platform_email: formData.get('platform_email') || null,
      province: formData.get('province') || null,
      // Impuesto del cliente. Cadena vacía = "IVA general", que en la base se
      // guarda como null: es lo que tienen 1.975 de los 2.044 clientes y lo
      // que `regimen_fiscal.regimen_de` interpreta como el 21% de siempre.
      //
      // `tax_rate` va emparejado a propósito: "no sujeta" es 0 por definición,
      // y guardar un tipo al lado dejaría la ficha diciendo dos cosas.
      tax_type: formData.get('tax_type') || null,
      tax_rate: formData.get('tax_type') === 'NO_SUJETO' ? 0 : null,
      // Vacío = "sin decidir", que en la base es null. Va aparte del impuesto
      // porque no tienen nada que ver: un canario puede pagar por recibo.
      payment_method: formData.get('payment_method') || null
    };

    // Se vuelve a mirar al guardar aunque ya se mirara al salir del campo: el
    // aviso de abajo puede ser de un NIF que luego se cambió. Avisa y deja
    // seguir, porque varios locales de una empresa comparten NIF.
    if (nifTocado(data.tax_id)) {
      const fichas = await buscarMismoNif(data.tax_id);
      setFichasMismoNif(fichas);
      const pregunta = preguntaAlGuardar(fichas, creandoCliente);
      if (pregunta && !(await confirmAsync(pregunta))) return;
    }

    if (creandoCliente) {
      try {
        // Sin estado: el servidor lo pone Activo.
        const { data: creado } = await axiosInstance.post('/customers', { ...data, type: 'customer' });
        toast.success('Cliente creado', {
          action: { label: 'Abrir ficha', onClick: () => handleViewDetail(creado.id) },
        });
        setDialogOpen(false);
        setEditingClient(null);
        setCreandoCliente(false);
        fetchClients();
      } catch (error) {
        toast.error(mensajeDeError(error, 'Error al crear el cliente'));
      }
      return;
    }

    try {
      // Optimistic update: PUT devuelve el doc actualizado, lo reflejamos en
      // el state local para que la lista cambie al instante sin esperar al GET.
      const { data: updated } = await axiosInstance.put(`/customers/${editingClient.id}`, data);
      setClients((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
      toast.success('Cliente actualizado correctamente');
      setDialogOpen(false);
      setEditingClient(null);
      // Red de seguridad: refresca por si hay campos calculados.
      fetchClients();
    } catch (error) {
      // `mensajeDeError` siempre devuelve texto. Pintar `detail` a pelo era la
      // pantalla en blanco del 7-sep: con un 422 ahi viene una lista de
      // objetos y React no sabe pintar un objeto.
      toast.error(mensajeDeError(error, 'Error al actualizar cliente'));
    }
  };


  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor, selecciona un archivo Excel (.xlsx o .xls)');
      return;
    }
    
    setImportFile(file);
    setLoadingPreview(true);
    
    // Generate preview
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axiosInstance.post('/customers/import-preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setPreviewData(response.data);
      toast.success(`Vista previa generada: ${response.data.total_rows} clientes encontrados`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al generar vista previa';
      toast.error(errorMsg);
      setImportFile(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Exportando clientes...');
      
      const response = await axiosInstance.get('/customers/export-excel', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Clientes exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar clientes');
    }
  };

  const handleExportCSV = async () => {
    try {
      toast.info('Exportando clientes...');
      
      const response = await axiosInstance.get('/customers/export-csv', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Clientes exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar clientes');
    }
  };

  const handleImport = async () => {
    if (!importFile || !previewData) {
      toast.error('Por favor, selecciona un archivo Excel');
      return;
    }

    // NEW: No elimina todos, solo agrega nuevos
    const message = previewData.new_count > 0
      ? `Se importarán ${previewData.new_count} clientes nuevos (los existentes no se modificarán)`
      : `No hay clientes nuevos para importar. ${previewData.existing_count} ya existen`;
    
    if (!(await confirmAsync({ title: 'Confirmar importación', description: `${message}\n\n¿Deseas continuar?`, confirmText: 'Importar' }))) {
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await axiosInstance.post('/customers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const successMessage = `✅ Importación exitosa!\n- ${response.data.imported_count} clientes nuevos importados\n- ${response.data.skipped_count} clientes existentes omitidos`;
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(
          `${successMessage}\n- ${response.data.errors.length} errores encontrados`,
          { duration: 8000 }
        );
        console.log('Errores de importación:', response.data.errors);
      } else {
        toast.success(message, { duration: 5000 });
      }

      setImportDialogOpen(false);
      setImportFile(null);
      setPreviewData(null);
      fetchClients();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al importar clientes';
      toast.error(errorMsg);
    } finally {
      setImporting(false);
    }
  };

  const handleXeroPickFile = () => {
    xeroFileRef.current?.click();
  };

  const handleXeroFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input para poder re-seleccionar el mismo archivo
    e.target.value = '';

    const ok = await confirmAsync({
      title: 'Importar desde Xero',
      description:
        `Se importará el CSV "${file.name}".\n\n` +
        `Los clientes se sincronizan por NIF/CIF:\n` +
        `• Si el CIF existe → se ACTUALIZAN los campos del CSV (IBAN, plataforma C10 y partner quedan intactos).\n` +
        `• Si el CIF no existe → se CREA cliente nuevo.\n\n` +
        `No se borra nada: los clientes que NO estén en el CSV se quedan como están.`,
      confirmText: 'Importar',
    });
    if (!ok) return;

    setXeroImporting(true);
    setXeroResult(null);
    setXeroDialogOpen(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(
        '/customers/import-xero',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setXeroResult(response.data);
      toast.success(
        `Xero: ${response.data.created} creados, ${response.data.updated} actualizados`
      );
      // Recargar lista
      fetchClients();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error importando CSV de Xero');
      setXeroResult({ error: detail || 'Error desconocido' });
    } finally {
      setXeroImporting(false);
    }
  };

  const handleUpdateBICs = async () => {
    if (!(await confirmAsync({ title: 'Actualizar BIC masivo', description: '¿Actualizar automáticamente los códigos BIC de todos los clientes con IBAN? Esto puede tardar unos segundos.' }))) {
      return;
    }

    try {
      toast.loading('Actualizando BICs...');
      const response = await axiosInstance.post('/customers/update-bics');
      
      toast.dismiss();
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(
          `BICs actualizados: ${response.data.updated_count}/${response.data.total_processed}\n${response.data.errors.length} clientes con errores`,
          { duration: 6000 }
        );
        console.log('Errores:', response.data.errors);
      } else {
        toast.success(`✅ ${response.data.updated_count} códigos BIC actualizados correctamente`);
      }
      
      fetchClients();
    } catch (error) {
      toast.dismiss();
      const errorMsg = error.response?.data?.detail || 'Error al actualizar BICs';
      toast.error(errorMsg);
    }
  };


  const handleCalculateBIC = async () => {
    // Obtener el IBAN del formulario
    const ibanInput = document.getElementById('sepa_iban');
    const bicInput = document.getElementById('sepa_bic');
    
    if (!ibanInput || !ibanInput.value) {
      toast.error('Por favor, introduce un IBAN primero');
      return;
    }

    const iban = ibanInput.value.trim();
    
    try {
      toast.loading('Calculando BIC...');
      const response = await axiosInstance.post('/customers/calculate-bic', {
        iban: iban
      });
      
      toast.dismiss();
      
      if (response.data.success) {
        // Poner el BIC en el campo
        if (bicInput) {
          bicInput.value = response.data.bic;
        }
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.dismiss();
      const errorMsg = error.response?.data?.detail || 'Error al calcular BIC';
      toast.error(errorMsg);
    }
  };

  const handleDarDeBaja = async (client) => {
    if (!(await confirmAsync({ title: 'Dar de baja cliente', description: `¿Seguro que quieres dar de baja al cliente "${client.name}"?`, confirmText: 'Dar de baja' }))) {
      return;
    }
    
    try {
      const { data } = await axiosInstance.patch(`/customers/${client.id}/dar-baja`);
      toast.success('Cliente dado de baja correctamente');
      if (data.plantillas_vivas > 0) {
        toast.warning(`Sigue con ${data.plantillas_vivas} ${data.plantillas_vivas === 1 ? 'plantilla activa' : 'plantillas activas'}: se le seguirá facturando hasta que las pares a mano.`);
      }
      fetchClients();
      fetchBajasConPlantilla();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al dar de baja el cliente';
      toast.error(errorMsg);
    }
  };

  const handleReactivar = async (client) => {
    if (!(await confirmAsync({ title: 'Reactivar cliente', description: `¿Reactivar al cliente "${client.name}"?`, confirmText: 'Reactivar' }))) {
      return;
    }
    
    try {
      await axiosInstance.patch(`/customers/${client.id}/reactivar`);
      toast.success('Cliente reactivado correctamente');
      fetchClients();
      fetchBajasConPlantilla();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al reactivar el cliente';
      toast.error(errorMsg);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!(await confirmAsync({ title: '⚠️ Borrar cliente permanentemente', description: `¿Totalmente seguro de BORRAR "${client.name}"?\n\nEsta acción NO se puede deshacer.`, confirmText: 'Borrar', destructive: true }))) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/customers/${client.id}`);
      toast.success('Cliente borrado correctamente');
      fetchClients();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al borrar el cliente';
      toast.error(errorMsg, { duration: 6000 });
    }
  };


  const getStatusBadge = (status) => {
    const statusConfig = crmStatuses.find(s => s.key === status);
    if (!statusConfig) return <Badge variant="outline" className="bg-gray-100 text-gray-800">{status}</Badge>;

    const colorMap = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    const colorClass = colorMap[statusConfig.color] || colorMap.gray;

    return (
      <Badge variant="outline" className={`${colorClass} border`}>
        {statusConfig.name}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    return type === 'customer' ? (
      <Badge className="bg-green-100 text-green-800">Cliente</Badge>
    ) : (
      <Badge className="bg-gray-100 text-gray-800">Lead</Badge>
    );
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-[#212B35] mb-2">Clientes</h1>
            <p className="text-[#212B35]/70">Gestiona tu cartera de leads y clientes</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleNuevo}
              className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
              data-testid="nuevo-cliente"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo cliente
            </Button>
            <Button
              onClick={handleUpdateBICs}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar BICs
            </Button>
            <Button
              onClick={handleExportExcel}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button
              onClick={() => setImportDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
            <Button
              onClick={handleXeroPickFile}
              disabled={xeroImporting}
              className="bg-[#13B5EA] hover:bg-[#0EA1D3] text-white"
              title="Importar clientes desde un CSV exportado de Xero (Contacts)"
            >
              <Upload className="w-4 h-4 mr-2" />
              {xeroImporting ? 'Importando...' : 'Importar desde Xero'}
            </Button>
            <input
              ref={xeroFileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleXeroFileSelected}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {bajasError && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="py-3 text-sm text-amber-900">
              No se ha podido comprobar si hay clientes de baja con plantillas activas.
            </CardContent>
          </Card>
        )}

        {bajasConPlantilla?.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="py-3 text-sm text-amber-900">
              <p className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="w-4 h-4" />
                Clientes de baja que se siguen facturando
              </p>
              <ul className="mt-2 space-y-1">
                {bajasConPlantilla.map((baja) => (
                  <li key={baja.id}>
                    <button type="button" className="underline" onClick={() => handleViewDetail(baja.id)}>
                      {baja.name}
                    </button>
                    {' · '}baja {baja.baja_fecha ? new Date(baja.baja_fecha).toLocaleDateString('es-ES') : 'sin fecha'}
                    {baja.baja_por ? ` por ${baja.baja_por}` : ''}
                    {' · '}próxima factura {new Date(baja.proxima_emision).toLocaleDateString('es-ES')}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label>Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Nombre, email, teléfono, empresa..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={typeFilter} 
                  onValueChange={(value) => {
                    setTypeFilter(value);
                    setStatusFilter('all'); // Reset estado al cambiar tipo
                    setPage(1); // Reset a página 1
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="lead">Leads</SelectItem>
                    <SelectItem value="customer">Clientes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select 
                  value={statusFilter} 
                  onValueChange={(value) => {
                    setStatusFilter(value);
                    setPage(1); // Reset a página 1 al cambiar filtro
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {typeFilter === 'customer' ? (
                      // Estados para clientes
                      <>
                        <SelectItem value="Activo">Activo</SelectItem>
                        <SelectItem value="Baja">Baja</SelectItem>
                      </>
                    ) : typeFilter === 'lead' ? (
                      // Estados para leads (del CRM)
                      crmStatuses.map(status => (
                        <SelectItem key={status.key} value={status.key}>
                          {status.name}
                        </SelectItem>
                      ))
                    ) : (
                      // Si no hay tipo seleccionado, mostrar todos
                      <>
                        <SelectItem value="Activo">Activo</SelectItem>
                        <SelectItem value="Baja">Baja</SelectItem>
                        {crmStatuses.map(status => (
                          <SelectItem key={status.key} value={status.key}>
                            {status.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {total > 0 && (
          <div className="text-sm text-gray-600 mb-2">
            {total} {total === 1 ? 'resultado' : 'resultados'} {searchTerm && `para "${searchTerm}"`}
          </div>
        )}

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        No se encontraron clientes
                      </td>
                    </tr>
                  ) : (
                    clients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{client.name}</div>
                          {client.fiscal_name && client.fiscal_name !== client.name && (
                            <div className="text-xs text-gray-500 mt-1">
                              {client.fiscal_name}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="flex items-center text-gray-600 mb-1">
                              <Mail className="w-3 h-3 mr-1" />
                              {client.email}
                            </div>
                            {client.phone && (
                              <>
                                <div className="flex items-center text-gray-600 mb-1">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {client.phone}
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const phoneNumber = client.phone.replace(/\s/g, '').replace(/-/g, '');
                                    window.open(`https://wa.me/${phoneNumber}`, '_blank');
                                  }}
                                  className="bg-green-500 hover:bg-green-600 text-white h-6 px-2 text-xs"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                  </svg>
                                  WhatsApp
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getTypeBadge(client.type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(client.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1.5">
                            <Button
                              size="sm"
                              className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white text-xs h-7"
                              onClick={() => handleViewDetail(client.id)}
                            >
                              Ver Detalle
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7"
                              onClick={() => handleEdit(client)}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Editar
                            </Button>
                            
                            {/* Mostrar "Dar de Baja" solo para clientes activos */}
                            {client.type === 'customer' && client.status !== 'Baja' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs h-7 bg-orange-600 hover:bg-orange-700"
                                onClick={() => handleDarDeBaja(client)}
                              >
                                Dar de Baja
                              </Button>
                            )}
                            
                            {/* Mostrar "Reactivar" solo para clientes de baja */}
                            {client.type === 'customer' && client.status === 'Baja' && (
                              <Button
                                size="sm"
                                className="text-xs h-7 bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleReactivar(client)}
                              >
                                Reactivar
                              </Button>
                            )}
                            
                            {/* Botón Borrar - siempre visible */}
                            <Button
                              size="sm"
                              variant="destructive"
                              className="text-xs h-7 bg-red-700 hover:bg-red-800"
                              onClick={() => handleDeleteClient(client)}
                            >
                              Borrar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Paginación */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between px-6 py-4 border-t">
                <div className="text-sm text-gray-600">
                  Mostrando página {page} de {totalPages} ({total} clientes en total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Anterior
                  </Button>
                  <div className="flex items-center px-3 text-sm font-medium">
                    {page}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{creandoCliente ? 'Nuevo cliente' : 'Editar Cliente'}</DialogTitle>
              <DialogDescription>
                {creandoCliente
                  ? 'Nace como cliente activo. La plataforma, el DNS y el mandato se hacen después desde su ficha.'
                  : 'Actualiza la información del cliente'}
              </DialogDescription>
            </DialogHeader>
            {editingClient && (
              <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input name="name" defaultValue={editingClient.name} required />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input name="email" type="email" defaultValue={editingClient.email} required />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input name="phone" defaultValue={editingClient.phone} />
                  </div>
                  <div>
                    <Label>Empresa</Label>
                    <Input name="company" defaultValue={editingClient.company} />
                  </div>
                </div>

                {/* Información Adicional */}
                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700">Información Adicional</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="trade_name">Nombre Comercial</Label>
                      <Input 
                        id="trade_name"
                        name="trade_name" 
                        defaultValue={editingClient.trade_name}
                        placeholder="Nombre con el que opera el negocio"
                      />
                    </div>
                    <div>
                      <Label htmlFor="platform_email">Email de la Plataforma</Label>
                      <Input 
                        id="platform_email"
                        name="platform_email" 
                        type="email"
                        defaultValue={editingClient.platform_email}
                        placeholder="email@ejemplo.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Email de acceso a la plataforma
                      </p>
                    </div>
                  </div>
                </div>

                {/* Un alta de aquí siempre es cliente: los leads entran por el CRM. */}
                {!creandoCliente && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipo</Label>
                      <Select name="type" defaultValue={editingClient.type}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="customer">Cliente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-lg mb-4">Datos de Facturación</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fiscal_name">Nombre Fiscal</Label>
                      <Input 
                        id="fiscal_name"
                        name="fiscal_name" 
                        defaultValue={editingClient.fiscal_name}
                        placeholder="Razón social para facturas"
                      />
                    </div>
                    <div>
                      <Label>NIF/CIF</Label>
                      <Input name="tax_id" defaultValue={editingClient.tax_id} onBlur={alSalirDelNif} />
                    </div>
                    {/* Lo que hacía la automatización de Xero al dar de alta. Se
                        dice también cuando NO hay otra: si solo aparece cuando hay
                        algo, no se sabe si ha mirado. */}
                    {fichasMismoNif !== undefined && (
                      <div className="col-span-2" data-testid="aviso-nif-repetido">
                        {fichasMismoNif === null ? (
                          <p className="text-xs text-slate-500">
                            No se ha podido comprobar si este NIF ya está en otra ficha.
                          </p>
                        ) : fichasMismoNif.length === 0 ? (
                          <p className="text-xs text-emerald-700">No hay otra ficha con este NIF.</p>
                        ) : (
                          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            <p className="font-semibold">
                              {fichasMismoNif.length === 1
                                ? 'Este NIF ya está en otra ficha'
                                : `Este NIF ya está en ${fichasMismoNif.length} fichas`}
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {fichasMismoNif.map((f) => (
                                <li key={f.id}>
                                  {/* En otra pestaña: aquí hay un formulario a medias. */}
                                  <a href={`/clients/${f.id}`} target="_blank" rel="noreferrer"
                                     className="underline hover:text-amber-700">
                                    {describirFicha(f)}
                                  </a>
                                </li>
                              ))}
                            </ul>
                            <p className="mt-1 text-xs text-amber-800">
                              Puede ser correcto si es otro local de la misma empresa. Si es el
                              mismo cliente, mejor abrir su ficha.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="col-span-2">
                      <Label htmlFor="billing_email">Email de Facturas</Label>
                      {/* `multiple` es lo que hace que el navegador acepte
                          varias separadas por coma. Con type="email" a secas
                          las daba por invalidas y no dejaba guardar. */}
                      <Input
                        id="billing_email"
                        name="billing_email"
                        type="email"
                        multiple
                        defaultValue={editingClient.billing_email}
                        placeholder="cliente@ejemplo.com, gestoria@asesores.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Email donde se envían las facturas. Puedes poner varios
                        separados por coma y llegará a todos.
                      </p>
                    </div>
                    <div>
                      <Label>Dirección</Label>
                      <Input name="address" defaultValue={editingClient.address} />
                    </div>
                    <div>
                      <Label>Código Postal</Label>
                      <Input name="postal_code" defaultValue={editingClient.postal_code} />
                    </div>
                    <div>
                      <Label>Ciudad</Label>
                      <Input name="city" defaultValue={editingClient.city} />
                    </div>
                    <div>
                      <Label>Provincia</Label>
                      <Input name="province" defaultValue={editingClient.province} placeholder="Ej: Madrid, Barcelona, Valencia..." />
                    </div>
                    <div>
                      <Label>País</Label>
                      <Input name="country" defaultValue={editingClient.country || 'España'} />
                    </div>
                    {/* El impuesto del cliente. Estaba solo en el diálogo de
                        "Datos para facturar", que se abre desde el CRM y desde
                        Presupuestos -- o sea, no desde la ficha, que es donde
                        se entra a cambiar los datos de un cliente. */}
                    <div className="col-span-2">
                      <Label htmlFor="tax_type">Impuesto de sus facturas</Label>
                      <select
                        id="tax_type"
                        name="tax_type"
                        defaultValue={editingClient.tax_type || ''}
                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                        data-testid="cliente-tax_type"
                      >
                        <option value="">IVA general (21%)</option>
                        <option value="NO_SUJETO">No sujeta a IVA (Canarias, Ceuta, Melilla, extranjero)</option>
                        <option value="IGIC">IGIC (Canarias)</option>
                        <option value="IPSI">IPSI (Ceuta y Melilla)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Se aplica a todas sus facturas, también a las repetitivas
                        que se generan solas cada mes.
                      </p>
                    </div>
                    {/* Cómo paga este cliente.
                        Hasta el 8-sep-2026 este dato no existía: se dedujo una
                        vez al importar de Paygold con "si el campo IBAN tiene
                        algo escrito, paga por recibo". Como no había dónde
                        apuntarlo, la gente lo escribió en el campo del IBAN
                        (CAIXABANKPROGRAMADO, STRIPE...) y esos clientes
                        quedaron marcados como domiciliados. */}
                    <div className="col-span-2">
                      <Label htmlFor="payment_method">Cómo paga este cliente</Label>
                      <select
                        id="payment_method"
                        name="payment_method"
                        defaultValue={editingClient.payment_method || ''}
                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                        data-testid="cliente-payment_method"
                      >
                        <option value="">Sin decidir</option>
                        <option value="sepa">Recibo domiciliado (se lo giramos nosotros)</option>
                        <option value="transferencia">Transferencia (nos lo transfiere él)</option>
                        <option value="programado">Pago programado (lo paga él por su cuenta)</option>
                        <option value="link">Link de pago con tarjeta</option>
                        <option value="efectivo">Efectivo</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Sale puesta al hacerle una factura, y se puede cambiar en
                        cada una. <strong>Pago programado</strong> nunca entra en una
                        remesa: girarle un recibo le cobraría dos veces.
                      </p>
                    </div>
                    <div>
                      <Label>Contacto Facturación</Label>
                      <Input name="billing_contact_name" defaultValue={editingClient.billing_contact_name} />
                    </div>
                    <div>
                      <Label>Teléfono Contacto</Label>
                      <Input name="billing_contact_phone" defaultValue={editingClient.billing_contact_phone} />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Notas</Label>
                  <Textarea name="notes" defaultValue={editingClient.notes} rows={3} />
                </div>

                <div className="border-t pt-4 space-y-3">
                  <h3 className="font-semibold text-sm">Alerta de Facturación</h3>
                  <div>
                    <Label>Nota de Facturación (opcional)</Label>
                    <Textarea 
                      name="billing_note" 
                      defaultValue={editingClient.billing_note} 
                      rows={2}
                      placeholder="Ej: Este cliente requiere descuento del 10%, Incluir código de proyecto..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Esta nota aparecerá como alerta al crear/editar facturas de este cliente
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="show_billing_alert"
                      name="show_billing_alert"
                      defaultChecked={editingClient.show_billing_alert}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="show_billing_alert" className="font-normal cursor-pointer">
                      Mostrar alerta al facturar este cliente
                    </Label>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3 bg-blue-50 -mx-6 px-6 py-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-6 bg-blue-600 rounded"></div>
                    <h3 className="font-semibold text-sm text-blue-900">Datos SEPA (Domiciliación Bancaria)</h3>
                  </div>
                  <p className="text-xs text-blue-700">
                    Rellena estos datos solo si este cliente tiene domiciliación bancaria SEPA
                  </p>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="sepa_iban">IBAN del Cliente</Label>
                      <Input
                        id="sepa_iban"
                        name="sepa_iban"
                        defaultValue={editingClient.sepa_iban}
                        placeholder="ES12 3456 7890 1234 5678 9012"
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Formato: ES + 22 dígitos
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="sepa_bic">BIC del Banco</Label>
                      <div className="flex gap-2">
                        <Input
                          id="sepa_bic"
                          name="sepa_bic"
                          defaultValue={editingClient.sepa_bic}
                          placeholder="SANTESMMXXX"
                          className="font-mono flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCalculateBIC}
                          className="bg-blue-50 hover:bg-blue-100 border-blue-200 whitespace-nowrap"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Calcular BIC
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Código SWIFT/BIC del banco del cliente (8 u 11 caracteres)
                      </p>
                    </div>

                    {editingClient.sepa_mandate_id && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800">
                          ✓ Este cliente tiene un mandato SEPA activo
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          ID Mandato: <span className="font-mono">{editingClient.sepa_mandate_id}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <Button type="submit" className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white">
                  {creandoCliente ? 'Crear cliente' : 'Guardar Cambios'}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Importar Clientes desde Excel</DialogTitle>
              <DialogDescription>
                Sube un archivo Excel con los datos de los clientes. Solo se importarán los clientes nuevos (NIF/CIF no existente).
                <br/>
                <span className="text-purple-600 font-medium">💡 Columna B debe contener el NIF/CIF</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <div className="text-sm text-gray-600 mb-2">
                  {importFile ? (
                    <span className="text-green-600 font-medium">✓ {importFile.name}</span>
                  ) : (
                    <>Selecciona un archivo Excel (.xlsx, .xls)</>
                  )}
                </div>
                <Input
                  id="excel-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportFile}
                  className="hidden"
                />
                <label htmlFor="excel-file">
                  <Button type="button" variant="outline" className="mt-2" asChild>
                    <span>{loadingPreview ? 'Cargando...' : 'Seleccionar Archivo'}</span>
                  </Button>
                </label>
              </div>

              {/* Preview Data */}
              {previewData && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-sm text-green-900 mb-2">
                    ✓ Vista Previa - {previewData.total_rows} clientes encontrados
                  </h4>
                  <p className="text-xs text-green-800 mb-3">
                    {previewData.new_count !== undefined ? (
                      <>
                        <strong>{previewData.new_count} clientes nuevos</strong> se importarán.{' '}
                        {previewData.existing_count > 0 && <>{previewData.existing_count} ya existen y se omitirán.</>}
                      </>
                    ) : (
                      `Se mostrarán los primeros 10 registros. Se importarán solo los clientes con NIF/CIF no existente.`
                    )}
                  </p>
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-green-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">#</th>
                          <th className="p-2 text-left">Nombre</th>
                          <th className="p-2 text-left">NIF/CIF</th>
                          <th className="p-2 text-left">Email</th>
                          <th className="p-2 text-left">Ciudad</th>
                          <th className="p-2 text-left">Teléfono</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview.map((row, idx) => (
                          <tr key={idx} className="border-t border-green-200">
                            <td className="p-2">{row.row_number}</td>
                            <td className="p-2">{row.name || '-'}</td>
                            <td className="p-2">{row.tax_id || '-'}</td>
                            <td className="p-2 text-xs">{row.billing_email || '-'}</td>
                            <td className="p-2">{row.city || '-'}</td>
                            <td className="p-2">{row.phone || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-blue-900 mb-2">Formato del Excel (sin fila de encabezado):</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li><strong>Columna A:</strong> Nombre comercial</li>
                  <li><strong>Columna B:</strong> NIF/CIF</li>
                  <li><strong>Columna C:</strong> Email de facturas</li>
                  <li><strong>Columnas D + E:</strong> Nombre y Apellido (se concatenan)</li>
                  <li><strong>Columna F:</strong> Dirección fiscal</li>
                  <li><strong>Columna G:</strong> Ciudad</li>
                  <li><strong>Columna H:</strong> Provincia</li>
                  <li><strong>Columna I:</strong> Código postal</li>
                  <li><strong>Columna J:</strong> País</li>
                  <li><strong>Columna K:</strong> Teléfono</li>
                  <li><strong>Columna L:</strong> IBAN del cliente</li>
                  <li><strong>Columna M:</strong> Nombre fiscal</li>
                  <li><strong>Columna N:</strong> Email de la plataforma</li>
                </ul>
                <p className="text-xs text-blue-700 mt-2">
                  ⚠️ Los campos vacíos se dejarán en blanco. Si hay errores de formato, se te notificará.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setImportDialogOpen(false);
                    setImportFile(null);
                    setPreviewData(null);
                  }}
                  disabled={importing}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleImport}
                  disabled={!previewData || importing}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {importing ? (
                    <>Importando...</>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Confirmar Importación ({previewData?.total_rows || 0} clientes)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Xero import result dialog */}
        <Dialog open={xeroDialogOpen} onOpenChange={setXeroDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Importación desde Xero</DialogTitle>
              <DialogDescription>
                {xeroImporting ? 'Procesando el CSV...' : 'Resultado del import'}
              </DialogDescription>
            </DialogHeader>
            {xeroImporting ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-[#13B5EA]" />
              </div>
            ) : xeroResult?.error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded text-red-800">
                {xeroResult.error}
              </div>
            ) : xeroResult ? (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-green-50 rounded border border-green-200">
                    <div className="text-2xl font-bold text-green-700">{xeroResult.created}</div>
                    <div className="text-xs text-green-800">Clientes nuevos</div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded border border-blue-200">
                    <div className="text-2xl font-bold text-blue-700">{xeroResult.updated}</div>
                    <div className="text-xs text-blue-800">Actualizados</div>
                  </div>
                </div>
                <div className="text-xs text-gray-600 pt-2">
                  <div>Filas procesadas: <strong>{xeroResult.total_rows}</strong></div>
                  {xeroResult.skipped_invalid > 0 && (
                    <div>Saltadas sin nombre: {xeroResult.skipped_invalid}</div>
                  )}
                  {xeroResult.skipped_duplicate_cif > 0 && (
                    <div>CIFs duplicados en CSV saltados: {xeroResult.skipped_duplicate_cif}</div>
                  )}
                </div>
                {xeroResult.errors?.length > 0 && (
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                    <div className="text-xs font-semibold text-orange-900 mb-1">
                      {xeroResult.errors.length} errores (primeros 10):
                    </div>
                    <ul className="text-xs text-orange-800 list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                      {xeroResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
            <div className="flex justify-end pt-4">
              <Button onClick={() => setXeroDialogOpen(false)} disabled={xeroImporting}>
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
